import {
  Caption,
  Chip,
  FacebookIcon,
  Headline,
  LinkedinIcon,
  mq,
  Stack,
  TwitterIcon
} from '@kvdbil/components';
import React from 'react';
import { useParams, useLocation } from 'react-router';
import styled from 'styled-components';
import Section from '~/App/shared/components/Layout/Section';
import Link from '~/App/shared/components/Link';
import {
  ArticlesByCategoriesDocument,
  ArticlesByCategoriesQuery,
  ArticlesByCategoryAndSlugWithRichTextDocument,
  ArticlesByCategoryAndSlugWithRichTextQuery,
  ArticlesForCategoryDocument,
  ArticlesForCategoryQuery,
  CategoryBySlugDocument,
  CategoryBySlugQuery,
  NewsletterSignupRecord
} from '~/config/generated/graphql';
import { useTranslation } from '~/Locale';
import ArticleCardListBlock from '../../components/ArticleCardListBlock';
import BlogBreadcrumbs from '../../components/BlogBreadcrumbs';
import BlogError from '../../components/BlogError';
import StatusPageLoading from '~/App/shared/components/StatusPageLoading';
import { blogDateFormat, getArticleStructuredData } from '../../helpers';
import { metaHelper } from '../../../../../helpers/seo';
import AdditionalCategoryArticlesBlocks from './AdditionalCategoryArticlesBlocks';
import {
  FacebookShareButton,
  TwitterShareButton,
  LinkedinShareButton
} from 'react-share';
import Head from '~/App/shared/components/Head';
import { Image } from 'react-datocms';
import StructuredText from '~/App/components/DatoCMS/StructuredText';
import { getBaseCanonicalUrl } from '~/helpers/baseCanonicalUrl';
import NotFound from '~/App/shared/components/NotFound';
import { RouteComponentProps } from '~/App/shared/interfaces/routes';
import {
  BlogArticlesByCategories,
  BlogArticlesByCategoryAndSlug,
  BlogArticlesForCategory,
  BlogCategoryBySlug
} from '~/App/shared/interfaces/store/CmsData';
import { useCmsData } from '~/App/shared/hooks/useCmsData';
import KvdHelperWrapper from '~/App/shared/components/KvdHelperWrapper';
import NewsletterTypeDispatcher from '../../components/NewsletterTypeDispatcher';
import withForcedUnmounting from '~/App/shared/hocs/withForcedUnmounting';

const HorizontalContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
`;
const SocialIcons = styled.ul`
  display: flex;
  font-size: 1.5rem;
  order: 2;
  align-items: center;
  a {
    color: ${({ theme }) => theme.colors.text.dark};

    &:hover {
      color: ${({ theme }) => theme.colors.grayscaleToned.dark3};
    }
  }
  ${mq('tablet')} {
    order: 3;
    justify-self: end;
  }
`;
const SocialIcon = styled.li`
  margin: 0 0.5em;

  a {
    color: ${({ theme }) => theme.colors.grayscaleToned.dark3};
    display: flex;
    align-content: center;
  }

  ${mq('tablet')} {
    &:last-child {
      margin-right: 0;
    }
  }
`;

const SimilarArticlesWrapper = styled.div`
  padding: 3.5rem 0;
  display: grid;
  grid-row-gap: 3.5rem;
  grid-template-columns: 100%;
`;

const StyledChip = styled(Chip)`
  cursor: pointer;
`;

const StructuredTextWrapper = styled.div`
  p {
    margin-bottom: 1.5rem;
  }
`;

const createCategoryBadges = (
  arr: {
    __typename?: 'CategoryRecord' | undefined;
    name?: string | null | undefined;
    slug?: string | null | undefined;
  }[]
) =>
  arr.map(category => (
    <Link key={category.name} to={`/ROUTES.BLOG/${category.slug}`}>
      <StyledChip variant="outline" color="primary" size="small">
        {category.name}
      </StyledChip>
    </Link>
  ));

const BlogArticle = ({ staticContext }: RouteComponentProps) => {
  const { t } = useTranslation();
  const { categorySlug, articleSlug } = useParams<{
    categorySlug: string;
    articleSlug: string;
  }>();
  const { pathname } = useLocation();

  const {
    data: categoryData,
    fetching: categoryFetching,
    error: categoryError
  } = useCmsData<CategoryBySlugQuery, BlogCategoryBySlug>({
    storeKey: 'blogCategoryBySlug',
    storeNestedKey: categorySlug,
    query: CategoryBySlugDocument,
    options: { variables: { slug: categorySlug } }
  });

  const {
    data: articleData,
    fetching: articleFetching,
    error: articleError
  } = useCmsData<
    ArticlesByCategoryAndSlugWithRichTextQuery,
    BlogArticlesByCategoryAndSlug
  >({
    storeKey: 'blogArticlesByCategoryAndSlug',
    storeNestedKey: `categoryId-${categoryData?.id}-articleSlug-${articleSlug}`,
    query: ArticlesByCategoryAndSlugWithRichTextDocument,
    options: {
      pause: !categoryData?.id,
      variables: {
        slug: articleSlug,
        categoryId: categoryData?.id
      }
    }
  });

  const { data: mainCategoryArticles } = useCmsData<
    ArticlesForCategoryQuery,
    BlogArticlesForCategory
  >({
    storeKey: 'blogArticlesForCategory',
    query: ArticlesForCategoryDocument,
    storeNestedKey: `categoryId-${categoryData?.id}-with-ingnore`,
    options: {
      pause: !categoryData?.id,
      variables: {
        categoryId: categoryData?.id,
        ignoreArticleSlugs: [articleSlug]
      }
    }
  });

  const additionalCategoryIds = articleData?.additionalCategories?.map(
    ({ id }) => String(id)
  );

  const { data: additionalCategoryArticles } = useCmsData<
    ArticlesByCategoriesQuery,
    BlogArticlesByCategories
  >({
    storeKey: 'blogArticlesByCategories',
    query: ArticlesByCategoriesDocument,
    storeNestedKey: `categoryIds-${additionalCategoryIds?.join('-')}`,
    options: {
      pause: !articleData,
      variables: {
        categoryIds: additionalCategoryIds,
        ignoreArticleSlugs: [articleSlug]
      }
    }
  });

  if (categoryFetching || articleFetching) {
    return <StatusPageLoading />;
  }

  if (categoryError || articleError) {
    return <BlogError />;
  }

  if (
    !categoryData ||
    categoryData?.notFound ||
    !articleData ||
    articleData?.notFound
  ) {
    return <NotFound staticContext={staticContext} />;
  }

  const article =
    articleData as ArticlesByCategoryAndSlugWithRichTextQuery['article'];

  const seoTitle = article?.seo?.title;
  const seoDescription = article?.seo?.description;
  const seoImage = article?.seo?.image?.responsiveImage?.src;
  const seoImageAlt = article?.seo?.image?.responsiveImage?.alt;
  const backupSeoImage = article?.miniatureImage?.responsiveImage?.src;
  const backupSeoImageAlt = article?.miniatureImage?.responsiveImage?.alt;

  const newsletterSection: Partial<NewsletterSignupRecord>[] =
    article?.newsletterSignupSection || [];

  const allMainCategoryArticles = mainCategoryArticles?.allArticles || [];

  const allAdditionalCategoryArticles =
    additionalCategoryArticles?.allArticles || [];

  return (
    <>
      <Head
        title={seoTitle ?? t('PAGE_META.DEFAULT_OG_TITLE')}
        meta={metaHelper({
          t,
          description: seoDescription,
          seoTitle,
          seoDescription,
          seoImage: seoImage ?? backupSeoImage,
          seoImageAlt: seoImageAlt ?? backupSeoImageAlt,
          url: getBaseCanonicalUrl(pathname)
        })}
      >
        {getArticleStructuredData(article)}
      </Head>
      <KvdHelperWrapper url={articleData?.kvdHelperUrl ?? ''}>
        <Section maxWidth={45} backgroundColor="light">
          <Stack spacing={1}>
            <BlogBreadcrumbs
              links={[
                {
                  component: (
                    <Link to={`/ROUTES.BLOG/${categorySlug}`}>
                      {categoryData?.name ?? ''}
                    </Link>
                  )
                },
                {
                  component: article?.title ?? ''
                }
              ]}
            />
            {article?.articleimage?.responsiveImage && (
              <Image data={article?.articleimage?.responsiveImage} />
            )}
            <Headline as="h1">{article?.title}</Headline>
            <HorizontalContainer>
              <Caption>{blogDateFormat(article?._firstPublishedAt, t)}</Caption>
              <SocialIcons>
                <SocialIcon>
                  <FacebookShareButton url={getBaseCanonicalUrl(pathname)}>
                    <FacebookIcon aria-label="kvd on facebook" />
                  </FacebookShareButton>
                </SocialIcon>
                <SocialIcon>
                  <TwitterShareButton url={getBaseCanonicalUrl(pathname)}>
                    <TwitterIcon aria-label="kvd on twitter" />
                  </TwitterShareButton>
                </SocialIcon>
                <SocialIcon>
                  <LinkedinShareButton url={getBaseCanonicalUrl(pathname)}>
                    <LinkedinIcon aria-label="kvd on linkedin" />
                  </LinkedinShareButton>
                </SocialIcon>
              </SocialIcons>
            </HorizontalContainer>
            <Stack direction={{ mobileS: 'column-reverse', tablet: 'column' }}>
              {article && categoryData && (
                <Stack direction="row" spacing={0.5}>
                  {createCategoryBadges([
                    categoryData,
                    ...article.additionalCategories
                  ])}
                </Stack>
              )}
              {article?.premable && <b>{article?.premable}</b>}
            </Stack>
            <StructuredText data={article?.body} />
          </Stack>
        </Section>
        {newsletterSection.length > 0 && newsletterSection[0] && (
          <Section spacing={2} backgroundColor="sand" maxWidth={35.5}>
            <NewsletterTypeDispatcher {...newsletterSection[0]} />
          </Section>
        )}
        <Section
          spacingTop={3.5}
          spacingBottom={0}
          backgroundColor="light"
          maxWidth={72}
        >
          <Headline>{t('Similar articles')}</Headline>
          <SimilarArticlesWrapper>
            {allMainCategoryArticles && allMainCategoryArticles.length > 0 && (
              <ArticleCardListBlock
                heading={categoryData?.name ?? ''}
                categorySlug={categorySlug}
                articles={allMainCategoryArticles}
                articlesLimit={4}
              />
            )}
            {allAdditionalCategoryArticles &&
              allAdditionalCategoryArticles.length > 0 && (
                <AdditionalCategoryArticlesBlocks
                  additionalIds={additionalCategoryIds}
                  articles={allAdditionalCategoryArticles}
                />
              )}
          </SimilarArticlesWrapper>
        </Section>

        <Section backgroundColor="light">
          {articleData?.seoInfo && (
            <StructuredText
              data={articleData?.seoInfo}
              wrapper={StructuredTextWrapper}
            />
          )}
        </Section>
      </KvdHelperWrapper>
    </>
  );
};

export default withForcedUnmounting(BlogArticle);
